// ============================================================================
// CONSULTANT PROMPT DATA NORMALIZER
// Shared by all consultant tab prompt builders
// ============================================================================

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const cleaned = String(value)
    .replace(/,/g, '')
    .replace(/%/g, '')
    .trim();

  if (!cleaned) return null;

  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
};

const firstMetricRow = (metricsHistory) =>
  Array.isArray(metricsHistory) && metricsHistory.length > 0 ? metricsHistory[0] : {};

const extractTotalTasks = (metricsHistory, fallback = 0) => {
  const row = firstMetricRow(metricsHistory);

  return (
    toNumber(row['Tasks']) ??
    toNumber(row['Work Items']) ??
    toNumber(row['Work items']) ??
    toNumber(row['Total Tasks']) ??
    toNumber(row['Total Work Items']) ??
    toNumber(row['work_items']) ??
    toNumber(row['total_tasks']) ??
    fallback
  );
};

const extractTotalVariants = (metricsHistory, fallback = 0) => {
  const row = firstMetricRow(metricsHistory);

  return (
    toNumber(row['Variants']) ??
    toNumber(row['Total Variants']) ??
    toNumber(row['variants']) ??
    toNumber(row['total_variants']) ??
    fallback
  );
};

const normalisePct = (num) => {
  if (num === null || num === undefined || !Number.isFinite(num)) return null;
  return Number(num.toFixed(2));
};

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.values(value).some(hasMeaningfulValue);
  return true;
};



const calculateDirectionalROI = ({ processSummary = {}, timeIntelligence = {}, transitionSignals = {}, hourlyRate = 90 }) => {
  const first = (...vals) => {
    for (const v of vals) {
      if (v !== null && v !== undefined && Number.isFinite(Number(v))) return Number(v);
    }
    return null;
  };

  const totalItems = first(processSummary.total_items, 0) || 0;
  const avgAdminHours = first(timeIntelligence.avg_admin_time_hours, timeIntelligence.avg_queue_time_hours, 0) || 0;
  const reworkRatePct = first(processSummary.rework_rate_percent, 0) || 0;
  const avgTouchHours = first(
    timeIntelligence.avg_touch_time_hours,
    Math.max((first(processSummary.avg_duration_hours, 0) || 0) * 0.15, 1),
    1
  ) || 1;
  const variantsTotal = first(processSummary.variants_total, 0) || 0;

  const topDelayTransitions = Array.isArray(transitionSignals.top_delay_transitions)
    ? transitionSignals.top_delay_transitions
    : [];

  const transitionDelayHours = topDelayTransitions
    .slice(0, 5)
    .reduce((sum, row) => sum + (toNumber(row.avg_duration_hours) || 0), 0);

  const adminDelayHours = Math.max(avgAdminHours, transitionDelayHours > 0 ? transitionDelayHours * 0.25 : 0);

  const delayCost = totalItems * adminDelayHours * hourlyRate;
  const reworkCost = totalItems * (reworkRatePct / 100) * avgTouchHours * hourlyRate;

  const complexityFactor =
    variantsTotal >= 150 ? 0.20 :
    variantsTotal >= 75  ? 0.12 :
    variantsTotal >= 30  ? 0.07 :
                           0.03;

  const complexityCost = totalItems * avgTouchHours * hourlyRate * complexityFactor;
  const totalCost = delayCost + reworkCost + complexityCost;

  return {
    assumptions: {
      hourly_rate: hourlyRate,
      currency: 'USD',
      model: 'v1-directional'
    },
    delay_cost: Number(delayCost.toFixed(2)),
    rework_cost: Number(reworkCost.toFixed(2)),
    complexity_cost: Number(complexityCost.toFixed(2)),
    total_cost: Number(totalCost.toFixed(2)),
    savings_low: Number((totalCost * 0.20).toFixed(2)),
    savings_high: Number((totalCost * 0.45).toFixed(2))
  };
};

const normalizeKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, ' ')
    .replace(/[^\w\s]/g, '');

const pickFirstMatchingValue = (row, candidates = []) => {
  if (!row || typeof row !== 'object') return null;

  const entries = Object.entries(row);
  for (const candidate of candidates) {
    const normalizedCandidate = normalizeKey(candidate);
    const match = entries.find(([key]) => normalizeKey(key) === normalizedCandidate);
    if (match && hasMeaningfulValue(match[1])) return match[1];
  }

  return null;
};

const firstPresent = (...vals) => {
  for (const v of vals) {
    if (v !== null && v !== undefined && v !== '' && v !== 'N/A') return v;
  }
  return null;
};

const firstNumber = (...vals) => {
  for (const v of vals) {
    const n = toNumber(v);
    if (n !== null) return n;
  }
  return null;
};

const getValue = (row, candidates = []) => pickFirstMatchingValue(row, candidates);

const normalizePercentValue = (value, { fractionIsLikely = true } = {}) => {
  const n = toNumber(value);
  if (n === null) return null;
  if (fractionIsLikely && n > 0 && n <= 1) return normalisePct(n * 100);
  return normalisePct(n);
};

const secondsToHoursStrict = (value) => {
  const n = toNumber(value);
  if (n === null) return null;
  return Number((n / 3600).toFixed(2));
};

const hoursValue = (value) => {
  const n = toNumber(value);
  if (n === null) return null;
  return Number(n.toFixed(2));
};

const splitTransitionName = (transition) => {
  const text = safeString(transition);
  if (!text) return { from: null, to: null };
  const parts = text.split(/\s*(?:->|→|-)\s*/).map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2) return { from: parts[0], to: parts[parts.length - 1] };
  return { from: null, to: null };
};

const transitionNameFromRow = (row = {}) => {
  const transition = firstPresent(
    row.Transition,
    row.transition,
    row.step,
    row.name,
    row.label
  );
  if (transition) return safeString(transition);
  const from = firstPresent(row['From Activity'], row.from_activity, row.from, row.f);
  const to = firstPresent(row['To Activity'], row.to_activity, row.to, row.t);
  return [from, to].filter(Boolean).join(' → ') || null;
};

const transitionVolume = (row = {}) => firstNumber(
  row['Work items'],
  row['Work Items'],
  row.WorkItems,
  row.work_items,
  row.workItems,
  row.items,
  row.Tasks,
  row.tasks,
  row.Count,
  row.count
) ?? 0;

const transitionPercent = (row = {}) => normalizePercentValue(firstPresent(
  row.pct_total_items,
  row.pct_total_tasks,
  row.percent_total_items,
  row.percent_total_tasks,
  row['% total Tasks'],
  row['% Total Tasks'],
  row['% total Work Items'],
  row['% Total Work Items'],
  row['% total Items'],
  row['% Total Items'],
  row['% total tasks'],
  row['% total work items']
));

const transitionAvgHours = (row = {}) => hoursValue(firstPresent(
  row.avg_hours,
  row.avg_duration_hours,
  row.avg_duration_hrs,
  row['Avg deviation (hrs)'],
  row['Avg Duration (hrs)'],
  row['Avg duration (hrs)'],
  row.avgDurationHours
));

const transitionStdHours = (row = {}) => hoursValue(firstPresent(
  row.std_hours,
  row.std_deviation_hours,
  row.std_deviation_hrs,
  row['Std deviation (hrs)'],
  row['Std Deviation (hrs)'],
  row.stdDurationHours
));

const isReworkRow = (row = {}) => {
  const value = firstPresent(row['Is rework?'], row['Is Rework'], row.is_rework, row.rework, row.loop, row.Loops, row.loops);
  if (typeof value === 'boolean') return value;
  if (toNumber(value) !== null) return toNumber(value) > 0;
  return ['yes', 'true', 'y'].includes(String(value || '').trim().toLowerCase());
};

const variantNameFromRow = (row = {}) => safeString(firstPresent(row.Variant, row.variant, row.path_name, row.path, row.name, row.label)) || null;
const variantIdFromRow = (row = {}) => firstPresent(row.ID, row.id, row.variant_id, row.variantId) ?? null;
const variantVolume = (row = {}) => firstNumber(row.Tasks, row.tasks, row.items, row.work_items, row['Work Items'], row['Work items']) ?? null;
const variantPercent = (row = {}) => normalizePercentValue(firstPresent(
  row.pct_total_items,
  row.pct_total_tasks,
  row.pct_total,
  row.percent_total_items,
  row['% Total Tasks'],
  row['% total Tasks'],
  row['% Total Work Items'],
  row['% total Work Items'],
  row['% Total Items'],
  row['% total Items']
));
const variantAvgHours = (row = {}) => hoursValue(firstPresent(row.avg_hours, row.avg_duration_hours, row.avg_duration_hrs, row['Avg Duration (hrs)'], row['Avg duration (hrs)']));
const variantStdHours = (row = {}) => hoursValue(firstPresent(row.std_hours, row.std_deviation_hours, row.std_deviation_hrs, row['Std Deviation (hrs)'], row['Std deviation (hrs)']));
const variantLoops = (row = {}) => firstNumber(row.Loops, row.loops, row.loop_count) ?? 0;

const SLA_FIELD_CANDIDATES = {
  total_count: ['SLA records', 'sla_total_count', 'Task SLA records', 'SLA count'],
  breached_count: ['SLA breached count', 'sla_breached_count', 'breached SLA count'],
  has_breach: ['SLA breached?', 'sla_has_breach', 'has_sla_breach', 'has breached'],
  completed_count: ['Completed SLA count', 'sla_completed_count'],
  cancelled_count: ['Cancelled SLA count', 'sla_cancelled_count'],
  business_elapsed_hours: ['SLA business elapsed hours', 'sla_business_elapsed_hours', 'business elapsed hours'],
  names: ['SLA names', 'sla_names', 'SLA name'],
  stages: ['SLA stages', 'sla_stages', 'SLA stage'],
};

const isTruthyText = (value) => ['yes', 'true', '1', 'y'].includes(String(value || '').trim().toLowerCase());

const splitMultiValue = (value) => String(value || '')
  .split(/\s*\|\s*|\s*,\s*/)
  .map(v => v.trim())
  .filter(Boolean);

const topValuesFromCounts = (counts = {}, limit = 8) => Object.entries(counts)
  .sort((a, b) => b[1] - a[1])
  .slice(0, limit)
  .map(([value, count]) => ({ value, count }));

const buildSlaSignals = ({ sampleItems = [], fieldUsage = [], totalTasks = 0 }) => {
  const rows = Array.isArray(sampleItems) ? sampleItems : [];
  const fieldRows = Array.isArray(fieldUsage) ? fieldUsage : [];
  const hasSlaFieldUsage = fieldRows.some(row => /\bsla\b|breach|business elapsed/i.test(String(row?.field_name || row?.field || row?.label || row?.name || '')));

  let itemsWithSla = 0;
  let breachedItems = 0;
  let totalSlaRecords = 0;
  let activeCount = 0;
  let completedCount = 0;
  let cancelledCount = 0;
  let elapsedSum = 0;
  let elapsedN = 0;
  const slaNameCounts = {};
  const stageCounts = {};

  rows.forEach(row => {
    const slaCount = toNumber(pickFirstMatchingValue(row, SLA_FIELD_CANDIDATES.total_count)) || 0;
    const breachCount = toNumber(pickFirstMatchingValue(row, SLA_FIELD_CANDIDATES.breached_count)) || 0;
    const hasBreach = isTruthyText(pickFirstMatchingValue(row, SLA_FIELD_CANDIDATES.has_breach)) || breachCount > 0;
    const active = toNumber(pickFirstMatchingValue(row, SLA_FIELD_CANDIDATES.active_count)) || 0;
    const completed = toNumber(pickFirstMatchingValue(row, SLA_FIELD_CANDIDATES.completed_count)) || 0;
    const cancelled = toNumber(pickFirstMatchingValue(row, SLA_FIELD_CANDIDATES.cancelled_count)) || 0;
    const elapsed = toNumber(pickFirstMatchingValue(row, SLA_FIELD_CANDIDATES.business_elapsed_hours));

    if (slaCount > 0 || breachCount > 0 || hasBreach || elapsed !== null) itemsWithSla += 1;
    if (hasBreach) breachedItems += 1;
    totalSlaRecords += slaCount;
    activeCount += active;
    completedCount += completed;
    cancelledCount += cancelled;
    if (elapsed !== null) { elapsedSum += elapsed; elapsedN += 1; }

    splitMultiValue(pickFirstMatchingValue(row, SLA_FIELD_CANDIDATES.names)).forEach(name => {
      slaNameCounts[name] = (slaNameCounts[name] || 0) + 1;
    });
    splitMultiValue(pickFirstMatchingValue(row, SLA_FIELD_CANDIDATES.stages)).forEach(stage => {
      stageCounts[stage] = (stageCounts[stage] || 0) + 1;
    });
  });

  const hasSlaEvidence = itemsWithSla > 0 || totalSlaRecords > 0 || hasSlaFieldUsage;
  if (!hasSlaEvidence) {
    return {
      available: false,
      source: hasSlaFieldUsage ? 'field_usage' : 'not_detected',
      guidance: 'No task_sla-derived metrics were detected in the current prompt sample.'
    };
  }

  return {
    available: true,
    source: rows.length ? 'task_sla_derived_sample_items' : 'field_usage',
    sampled_items: rows.length,
    items_with_sla: itemsWithSla,
    items_with_sla_percent: rows.length ? normalisePct((itemsWithSla / rows.length) * 100) : null,
    total_sla_records: totalSlaRecords || null,
    avg_sla_records_per_item: itemsWithSla ? Number((totalSlaRecords / itemsWithSla).toFixed(2)) : null,
    breached_items: breachedItems,
    breached_items_percent: itemsWithSla ? normalisePct((breachedItems / itemsWithSla) * 100) : null,
    active_sla_count: activeCount || null,
    completed_sla_count: completedCount || null,
    cancelled_sla_count: cancelledCount || null,
    avg_business_elapsed_hours: elapsedN ? Number((elapsedSum / elapsedN).toFixed(2)) : null,
    top_sla_names: topValuesFromCounts(slaNameCounts, 8),
    top_sla_stages: topValuesFromCounts(stageCounts, 8),
    prompt_guidance: [
      'Use this SLA evidence for ServiceNow outputs when assessing breach risk, escalation design, assignment/routing effectiveness, delay hotspots, and Flow Designer automation opportunities.',
      'Treat SLA signals as task_sla-derived operational evidence, not generic duration fields.'
    ],
  };
};

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter((v) => hasMeaningfulValue(v));
  if (value === null || value === undefined || value === '') return [];
  if (typeof value === 'string') {
    return value
      .split(/[;,|]/)
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [value];
};

const safeString = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};


const humanizeFieldLabel = (value) => {
  const raw = safeString(value);
  if (!raw) return '';
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/(^|\s)\S/g, (m) => m.toUpperCase());
};

const STATUS_LIFECYCLE_FIELD_KEYS = new Set([
  'status',
  'state',
  'workflow status',
  'workflow state',
  'incident state',
  'incident status',
  'change state',
  'stage',
  'lifecycle',
]);

const isLifecycleStatusFieldName = (value = '') => {
  const v = normalizeKey(value);
  if (!v) return false;
  if (STATUS_LIFECYCLE_FIELD_KEYS.has(v)) return true;
  return /(^|\b)(status|state|stage|lifecycle)(\b|$)/i.test(v);
};

const classifyLifecycleStatusValue = (value = '') => {
  const raw = safeString(value);
  const v = normalizeKey(raw);
  const has = (...words) => words.some((word) => v.includes(normalizeKey(word)));

  let operational_type = 'unclassified';
  let time_bucket = 'unclassified';
  let reason = 'No lifecycle/status keyword matched';
  let confidence = 0.4;

  if (has('start', 'end')) {
    operational_type = 'synthetic'; time_bucket = 'no_time'; reason = 'Synthetic process node'; confidence = 1;
  } else if (has('closed', 'cancelled', 'canceled', 'invalid', 'rejected', 'duplicate', 'withdrawn')) {
    operational_type = 'closed'; time_bucket = 'terminal'; reason = 'Terminal lifecycle state'; confidence = 0.95;
  } else if (has('resolved', 'complete', 'completed', 'done', 'fulfilled', 'restored')) {
    operational_type = 'resolved'; time_bucket = 'admin'; reason = 'Resolved / closure-admin lifecycle state'; confidence = 0.9;
  } else if (has('pending', 'awaiting', 'waiting', 'blocked', 'hold', 'on hold', 'deferred', 'paused')) {
    operational_type = 'waiting'; time_bucket = 'wait'; reason = 'Waiting / dependency lifecycle state'; confidence = 0.88;
  } else if (has('assigned', 'active', 'work in progress', 'in progress', 'working', 'investigating', 'implementing', 'processing')) {
    operational_type = 'active'; time_bucket = 'touch'; reason = 'Active work lifecycle state'; confidence = 0.9;
  } else if (has('new', 'open', 'opened', 'triage', 'to do', 'todo', 'queued', 'queue', 'backlog', 'unassigned')) {
    operational_type = 'intake'; time_bucket = 'queue'; reason = 'Intake / queue lifecycle state'; confidence = 0.86;
  }

  return {
    activity: raw,
    status: raw,
    operational_type,
    time_bucket,
    confidence,
    match_source: 'normalizer_lifecycle_default',
    reason,
  };
};

const uniqueLifecycleValuesFromTransitions = (transitionsJSON = []) => {
  const values = new Set();
  (Array.isArray(transitionsJSON) ? transitionsJSON : []).forEach((row) => {
    [
      row?.from_activity,
      row?.to_activity,
      row?.from,
      row?.to,
      row?.['From Activity'],
      row?.['To Activity'],
      row?.['From'],
      row?.['To'],
    ].forEach((value) => {
      const text = safeString(value);
      if (text) values.add(text);
    });

    const transitionText = safeString(row?.transition || row?.Transition);
    if (transitionText && transitionText.includes('->')) {
      transitionText.split(/\s*->\s*/).forEach((part) => {
        const text = safeString(part);
        if (text) values.add(text);
      });
    }
  });
  return [...values].filter(Boolean);
};

const countBy = (rows = [], field) => rows.reduce((acc, row) => {
  const value = row?.[field] || 'unknown';
  acc[value] = (acc[value] || 0) + 1;
  return acc;
}, {});

const inferActivityKind = (fieldOrLabel = '') => {
  const v = normalizeKey(fieldOrLabel);
  if (!v) return 'unknown';
  if (isLifecycleStatusFieldName(v)) return 'lifecycle_status';
  if (v === 'assignment group' || v === 'assignmentgroup' || v === 'resolver group routing' || v === 'resolver_group_routing' || v === 'routing group') return 'routing_group';
  if (v === 'assigned to' || v === 'assignee' || v === 'assignedto' || v === 'assignee ownership' || v === 'assignee_ownership' || v === 'ownership') return 'ownership';
  if (v.includes('approval')) return 'approval_state';
  if (v.includes('priority') || v.includes('classification') || v.includes('severity') || v.includes('impact') || v.includes('urgency') || v.includes('category')) return 'classification';
  return 'custom_activity_field';
};

const buildActivityContext = ({ activityContext = null, activityType = null, activityField = null, activityLabel = null } = {}) => {
  const provided = activityContext && typeof activityContext === 'object' ? activityContext : {};
  const field = safeString(provided.activity_field || provided.field || activityField || activityType || '');
  const label = safeString(provided.activity_label || provided.label || activityLabel || humanizeFieldLabel(field) || field || 'Selected activity');
  const kind = safeString(provided.activity_kind || provided.analysis_type || provided.type || provided.kind || inferActivityKind(field || label));
  const interpretationByKind = {
    lifecycle_status: 'Activities represent lifecycle/status progression through the work item workflow.',
    routing_group: 'Activities represent routing or handoff between responsible resolver groups or queues. Treat elapsed time as resolver-group dwell/ownership time, not literal lifecycle queue time.',
    ownership: 'Activities represent ownership or assignment changes between people. Treat elapsed time as assignee dwell/ownership time, not lifecycle queue time.',
    approval_state: 'Activities represent approval-state movement rather than lifecycle status movement.',
    classification: 'Activities represent changes in a classification field; interpret the map as segmentation/escalation movement, not a normal workflow lifecycle.',
    custom_activity_field: 'Activities represent the selected audited field; interpret paths according to that field rather than assuming status progression.',
    unknown: 'Activities represent the selected process-mining activity field; do not assume they are statuses.'
  };

  const normalizedKind = kind || 'unknown';
  const validMetricFamiliesByKind = {
    lifecycle_status: ['lifecycle', 'status', 'wait', 'queue', 'touch', 'admin', 'rework', 'bottleneck'],
    routing_group: ['routing', 'handoff', 'resolver_group_dwell', 'fragmentation', 'escalation'],
    ownership: ['ownership', 'reassignment', 'assignee_dwell', 'workload_concentration'],
    approval_state: ['approval_dwell', 'approval_loops', 'approval_handoffs'],
    classification: ['classification_stability', 'escalation', 'data_quality', 'category_concentration'],
    custom_activity_field: ['activity_dwell', 'transitions', 'variants'],
    unknown: ['activity_dwell', 'transitions', 'variants'],
  };

  const invalidInterpretations = normalizedKind === 'lifecycle_status'
    ? []
    : [
        'Do not call activity values statuses.',
        'Do not treat queue/wait/touch/admin buckets as literal lifecycle-status evidence.',
        'Do not infer lifecycle status progression unless the activity type is Lifecycle / Status.',
      ];

  return {
    platform: safeString(provided.platform || ''),
    table: safeString(provided.table || ''),
    activity_field: field,
    activity_label: label,
    activity_kind: normalizedKind,
    analysis_type: normalizedKind,
    analysis_type_label: safeString(provided.analysis_type_label || provided.type_label || ''),
    interpretation: safeString(provided.interpretation) || interpretationByKind[normalizedKind] || interpretationByKind.unknown,
    valid_metric_families: Array.isArray(provided.valid_metric_families) && provided.valid_metric_families.length
      ? provided.valid_metric_families
      : validMetricFamiliesByKind[normalizedKind] || validMetricFamiliesByKind.unknown,
    invalid_interpretations: Array.isArray(provided.invalid_interpretations) && provided.invalid_interpretations.length
      ? provided.invalid_interpretations
      : invalidInterpretations,
  };
};

const buildActivityAndStatusModels = (normalized, activityContext) => {
  const mappings = Array.isArray(normalized.statusTypes?.mappings) ? normalized.statusTypes.mappings : [];
  const summarySource = normalized.statusTypes?.summary || {};
  const activities = mappings.map((item) => ({
    activity: item.activity || item.status || item.statusName || null,
    operational_type: item.operational_type || item.operationalType || null,
    time_bucket: item.time_bucket || item.timeBucket || null,
    confidence: item.confidence ?? null,
    match_source: item.match_source || item.matchSource || null,
    reason: item.reason || null,
  }));

  const activityModel = {
    summary: {
      total_activities: summarySource.total_activities ?? summarySource.total_statuses ?? activities.length,
      activity_kind: activityContext.activity_kind,
      activity_field: activityContext.activity_field,
      activity_label: activityContext.activity_label,
      by_operational_type: summarySource.by_operational_type || {},
      by_time_bucket: summarySource.by_time_bucket || {},
    },
    activities,
    transition_profile: normalized.statusTypes?.transition_profile || null,
  };

  const isLifecycle = activityContext.activity_kind === 'lifecycle_status';
  const statusModel = isLifecycle
    ? {
        summary: {
          total_statuses: activityModel.summary.total_activities,
          by_operational_type: activityModel.summary.by_operational_type,
          by_time_bucket: activityModel.summary.by_time_bucket,
        },
        statuses: activities.map((item) => ({ ...item, status: item.activity })),
        transition_profile: activityModel.transition_profile,
      }
    : null;

  return { activityModel, statusModel, isLifecycle };
};


const contextualizeTimeIntelligence = (timeIntelligence = {}, activityContext = {}) => {
  const kind = activityContext?.activity_kind || 'unknown';
  if (kind === 'lifecycle_status') {
    return {
      ...timeIntelligence,
      interpretation_scope: 'lifecycle_status',
      interpretation_note: 'Time buckets can be interpreted as lifecycle queue/wait/touch/admin evidence.',
    };
  }

  const validity = { ...(timeIntelligence.validity || {}) };
  ['avg_touch_time_hours', 'avg_wait_time_hours', 'avg_admin_time_hours', 'avg_queue_time_hours', 'flow_efficiency_percent']
    .forEach((key) => { validity[key] = false; });

  return {
    ...timeIntelligence,
    avg_touch_time_hours: null,
    avg_wait_time_hours: null,
    avg_admin_time_hours: null,
    avg_queue_time_hours: null,
    flow_efficiency_percent: null,
    validity,
    interpretation_scope: kind,
    interpretation_note: kind === 'routing_group'
      ? 'Lifecycle queue/wait/touch/admin time buckets are disabled because the activity field is resolver-group routing. Use cycle/resolution time and resolver-group dwell/handoff evidence instead.'
      : kind === 'ownership'
        ? 'Lifecycle queue/wait/touch/admin time buckets are disabled because the activity field is assignee ownership. Use cycle/resolution time and ownership/reassignment evidence instead.'
        : 'Lifecycle queue/wait/touch/admin time buckets are disabled because the activity field is not a lifecycle status field.',
  };
};

const pct = (part, whole) => {
  if (!Number.isFinite(part) || !Number.isFinite(whole) || whole <= 0) return null;
  return normalisePct((part / whole) * 100);
};

const topNFromMap = (obj = {}, limit = 8) =>
  Object.entries(obj)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([label, count]) => ({ label, count }));

const FIELD_NAME_CANDIDATES = ['field', 'field name', 'name', 'fieldname'];
const FIELD_POPULATION_CANDIDATES = [
  'population_rate',
  'population rate',
  'pct_populated',
  '% populated',
  '% population',
  'usage_percent',
  'usage %',
  'percent populated',
];
const FIELD_DISTINCT_CANDIDATES = [
  'distinct_values',
  'distinct values',
  'unique_values',
  'unique values',
  'cardinality',
];
const FIELD_EMPTY_CANDIDATES = [
  'empty_count',
  'blank_count',
  'null_count',
  'missing_count',
];

const EXCLUDED_SAMPLE_ITEM_FIELDS = new Set([
  'id',
  'key',
  'issue key',
  'task #',
  'task number',
  'number',
  'sys id',
  'sys_id',
  'summary',
  'short description',
  'description',
  'created',
  'created on',
  'updated',
  'updated on',
  'resolved',
  'resolution date',
  'closed at',
  'start date',
  'due date',
  'status',
  'state',
  'variant id',
  'variant_id',
  'variant',
  'duration',
  'work item duration',
  'lead time',
  'cycle time',
]);

const EXCLUDED_EVIDENCE_FIELD_PATTERNS = [
  /(^|\b)task\s*#/i,
  /(^|\b)sys[_\s-]?id\b/i,
  /(^|\b)variant(\s+id)?\b/i,
  /(^|\b)duration\b/i,
  /(^|\b)(lead|cycle|touch|wait|queue|admin)\s*time\b/i,
  /(^|\b)avg\s+/i,
  /(^|\b)std(andard)?\s+deviation\b/i,
  /(^|\b)rework\b/i,
  /(^|\b)flow\s+efficiency\b/i,
  /(^|\b)sla\b/i,
  /breach/i,
  /business\s+elapsed/i,
  /business\s+percentage/i,
  /records?$/i,
  /(^|\b)task\s*id\b/i,
];

const normalizeFieldName = (value) => safeString(value);

const isExcludedEvidenceField = (fieldName = '') => {
  const raw = safeString(fieldName);
  if (!raw) return true;
  const normalized = normalizeKey(raw);
  if (EXCLUDED_SAMPLE_ITEM_FIELDS.has(normalized)) return true;
  return EXCLUDED_EVIDENCE_FIELD_PATTERNS.some((pattern) => pattern.test(raw) || pattern.test(normalized));
};

const isCompletelyUnpopulatedFieldUsageRow = (row = {}) => {
  const populatedPct = toNumber(row?.populated_pct ?? row?.population_rate);
  const populatedItems = toNumber(row?.populated_items);
  const missingCount = toNumber(row?.missing_count);
  const topValues = Array.isArray(row?.top_values) ? row.top_values : [];
  const hasTopValues = topValues.some((item) => hasMeaningfulValue(item?.value ?? item?.label));

  // Only suppress fields that have no observed values and no sample evidence.
  // Sparse but selected/populated fields are retained.
  if (row?.source && String(row.source).includes('sample_items')) return false;
  if (hasTopValues) return false;
  if (populatedPct === 0) return true;
  if (populatedItems === 0 && missingCount !== null) return true;
  return false;
};

const mergeTopValues = (sampleValues = [], fieldUsageValues = []) => {
  const counts = {};

  sampleValues.forEach((item) => {
    const label = safeString(item?.label ?? item?.value);
    const count = toNumber(item?.count) ?? 0;
    if (!label || count <= 0) return;
    counts[label] = Math.max(counts[label] || 0, count);
  });

  fieldUsageValues.forEach((item) => {
    const label = safeString(item?.label ?? item?.value);
    const count = toNumber(item?.count ?? item?.tasks) ?? 0;
    if (!label || count <= 0) return;
    counts[label] = Math.max(counts[label] || 0, count);
  });

  return topNFromMap(counts, 8);
};

const calculateDominancePercent = (topValues = [], populatedItems = null) => {
  if (!Array.isArray(topValues) || topValues.length === 0) return null;
  const topCount = toNumber(topValues[0]?.count ?? topValues[0]?.tasks);
  if (topCount === null || populatedItems === null || populatedItems <= 0) return null;
  return pct(topCount, populatedItems);
};

const estimateFieldSignalScore = (summary = {}) => {
  const populatedPct = toNumber(summary.populated_pct) ?? 0;
  const distinctValues = toNumber(summary.distinct_values) ?? (Array.isArray(summary.top_values) ? summary.top_values.length : 0) ?? 0;
  const dominancePct = toNumber(summary.dominance_pct);

  let score = populatedPct * 0.55;
  score += Math.min(distinctValues, 12) * 4;

  if (dominancePct !== null) {
    if (dominancePct >= 95) score -= 12;
    else if (dominancePct >= 85) score -= 7;
    else if (dominancePct <= 60) score += 4;
  }

  return Number(score.toFixed(2));
};

const inferClassificationStrength = (summary = {}) => {
  const populatedPct = toNumber(summary.populated_pct);
  const distinctValues = toNumber(summary.distinct_values) ?? (Array.isArray(summary.top_values) ? summary.top_values.length : 0);
  const dominancePct = toNumber(summary.dominance_pct);

  if (populatedPct === null) return 'unknown';
  if (populatedPct < 40) return 'weak';
  if (populatedPct < 70) return 'moderate';
  if (distinctValues <= 1 && dominancePct !== null && dominancePct >= 95) return 'weak';
  if (distinctValues <= 1) return 'moderate';
  return 'strong';
};

const buildSampleItemFieldSummaries = (sampleItems = []) => {
  const rows = Array.isArray(sampleItems) ? sampleItems : [];
  const sampleSize = rows.length;
  const fieldMap = {};

  rows.forEach((row) => {
    if (!row || typeof row !== 'object') return;

    Object.entries(row).forEach(([rawField, rawValue]) => {
      const field = normalizeFieldName(rawField);
      if (!field || isExcludedEvidenceField(field)) return;

      if (!fieldMap[field]) {
        fieldMap[field] = {
          populated_items: 0,
          value_counts: {},
        };
      }

      const values = toArray(rawValue).map(safeString).filter(Boolean);
      if (values.length === 0) return;

      fieldMap[field].populated_items += 1;
      values.forEach((value) => {
        fieldMap[field].value_counts[value] = (fieldMap[field].value_counts[value] || 0) + 1;
      });
    });
  });

  const summaries = Object.entries(fieldMap).map(([field_name, data]) => {
    const top_values = topNFromMap(data.value_counts, 8);
    const populated_items = data.populated_items;
    const populated_pct = pct(populated_items, sampleSize);
    const unclassified_items = Math.max(sampleSize - populated_items, 0);
    const unclassified_pct = pct(unclassified_items, sampleSize);
    const distinct_values = Object.keys(data.value_counts).length;
    const dominance_pct = calculateDominancePercent(top_values, populated_items);

    return {
      field_name,
      populated_items,
      populated_pct,
      unclassified_items,
      unclassified_pct,
      distinct_values,
      top_values,
      dominance_pct,
      source: 'sample_items',
    };
  });

  return {
    sample_size: sampleSize,
    fields: summaries.sort((a, b) => (b.populated_items ?? 0) - (a.populated_items ?? 0)),
  };
};

const extractFieldUsageInsights = (fieldUsage = [], totalTasks = 0, sampleItems = []) => {
  const rows = Array.isArray(fieldUsage) ? fieldUsage : [];
  const sampleFieldSummaries = buildSampleItemFieldSummaries(sampleItems);
  const sampleFieldMap = Object.fromEntries(
    sampleFieldSummaries.fields.map((field) => [normalizeKey(field.field_name), field])
  );

  const normalizedRows = rows
    .map((row) => {
      const field_name = normalizeFieldName(pickFirstMatchingValue(row, FIELD_NAME_CANDIDATES));
      const values = Array.isArray(row?.values)
        ? row.values.map((valueRow) => ({
            value: safeString(valueRow?.value),
            tasks: toNumber(valueRow?.tasks),
            perc_total_tasks: toNumber(valueRow?.perc_total_tasks),
          })).filter((item) => item.value)
        : [];

      const explicitPopulationRate = toNumber(pickFirstMatchingValue(row, FIELD_POPULATION_CANDIDATES));
      const explicitDistinctValues = toNumber(pickFirstMatchingValue(row, FIELD_DISTINCT_CANDIDATES));
      const explicitMissingCount = toNumber(pickFirstMatchingValue(row, FIELD_EMPTY_CANDIDATES));
      const sampleSummary = sampleFieldMap[normalizeKey(field_name)] || null;
      const valueTaskTotal = values.reduce((sum, item) => sum + (toNumber(item.tasks) || 0), 0);

      // `row.values` is a ranked/top-values distribution, not necessarily a
      // complete field population profile. Do not sum top values and infer that
      // the remainder of tasks are missing the field. This caused fields such
      // as ServiceNow close_code to be reported as "30% missing" when the
      // omitted 30% was simply long-tail classification values.
      const populated_items = sampleSummary?.populated_items
        ?? (explicitPopulationRate !== null && totalTasks > 0 ? Math.round((explicitPopulationRate / 100) * totalTasks) : null)
        ?? null;
      const populated_pct = sampleSummary?.populated_pct
        ?? explicitPopulationRate
        ?? null;
      const distinct_values = sampleSummary?.distinct_values ?? explicitDistinctValues ?? (values.length || null);
      const missing_count = sampleSummary?.unclassified_items ?? explicitMissingCount ?? null;
      const top_values = mergeTopValues(sampleSummary?.top_values || [], values);
      const dominance_pct = calculateDominancePercent(top_values, populated_items);
      const value_coverage_items = valueTaskTotal > 0 ? valueTaskTotal : null;
      const value_coverage_pct = totalTasks > 0 && valueTaskTotal > 0 ? normalisePct((valueTaskTotal / totalTasks) * 100) : null;

      return {
        ...row,
        field_name: field_name || null,
        type: safeString(row?.type) || null,
        values,
        populated_items,
        population_rate: populated_pct,
        populated_pct,
        distinct_values,
        missing_count,
        top_values,
        dominance_pct,
        value_coverage_items,
        value_coverage_pct,
        value_coverage_note: value_coverage_pct !== null
          ? 'Top-value coverage only; do not interpret as field population or missing-rate evidence.'
          : null,
        source: sampleSummary ? 'sample_items+field_usage' : 'field_usage',
      };
    })
    .filter((row) => row.field_name && !isExcludedEvidenceField(row.field_name) && !isCompletelyUnpopulatedFieldUsageRow(row));

  const sampleOnlyRows = sampleFieldSummaries.fields
    .filter((field) => !isExcludedEvidenceField(field.field_name) && !normalizedRows.some((row) => normalizeKey(row.field_name) === normalizeKey(field.field_name)))
    .map((field) => ({
      field_name: field.field_name,
      type: null,
      values: [],
      populated_items: field.populated_items,
      population_rate: field.populated_pct,
      populated_pct: field.populated_pct,
      distinct_values: field.distinct_values,
      missing_count: field.unclassified_items,
      top_values: field.top_values,
      dominance_pct: field.dominance_pct,
      source: 'sample_items',
    }));

  const allFieldSummaries = [...normalizedRows, ...sampleOnlyRows]
    .map((field) => ({
      ...field,
      classification_strength: inferClassificationStrength(field),
      signal_score: estimateFieldSignalScore(field),
    }))
    .sort((a, b) => (b.signal_score ?? -Infinity) - (a.signal_score ?? -Infinity));

  const sparse_fields = allFieldSummaries
    .filter((r) => r.populated_pct !== null && r.populated_pct < 50)
    .sort((a, b) => (a.populated_pct ?? 999) - (b.populated_pct ?? 999))
    .slice(0, 10)
    .map((r) => ({
      field_name: r.field_name,
      population_rate: r.populated_pct,
      distinct_values: r.distinct_values,
      missing_count: r.missing_count,
      top_values: r.top_values,
    }));

  const highly_used_fields = allFieldSummaries
    .filter((r) => r.populated_pct !== null)
    .sort((a, b) => (b.populated_pct ?? -1) - (a.populated_pct ?? -1))
    .slice(0, 10)
    .map((r) => ({
      field_name: r.field_name,
      population_rate: r.populated_pct,
      distinct_values: r.distinct_values,
      missing_count: r.missing_count,
      top_values: r.top_values,
    }));

  const classification_fields = allFieldSummaries.slice(0, 12).map((r) => ({
    attribute: r.field_name,
    field_name: r.field_name,
    population_rate: r.populated_pct,
    distinct_values: r.distinct_values,
    missing_count: r.missing_count,
    dominance_pct: r.dominance_pct,
    classification_strength: r.classification_strength,
    signal_score: r.signal_score,
    top_values: r.top_values,
    source: r.source,
  }));

  return {
    total_field_rows: allFieldSummaries.length,
    sparse_fields,
    highly_used_fields,
    classification_fields,
    field_usage_highlights: allFieldSummaries.slice(0, 20),
    notes: [
      totalTasks > 0 && allFieldSummaries.length === 0 ? 'Field usage model not provided' : null,
      sampleFieldSummaries.sample_size > 0 ? `Sample item field analysis derived from ${sampleFieldSummaries.sample_size} items` : null,
    ].filter(Boolean),
  };
};

const summarizeSampleItemAttributes = (sampleItems = []) => {
  const summary = buildSampleItemFieldSummaries(sampleItems);
  const observations = [];

  summary.fields.forEach((field) => {
    if (field.unclassified_pct !== null && field.unclassified_pct >= 40) {
      observations.push({
        type: 'missing_classification',
        attribute: field.field_name,
        message: `${field.field_name} is missing for ${field.unclassified_pct}% of sampled work items`,
      });
    }

    if ((field.top_values || []).length === 1 && field.populated_pct !== null && field.populated_pct >= 80) {
      observations.push({
        type: 'single_dominant_category',
        attribute: field.field_name,
        message: `${field.field_name} appears heavily concentrated in one category`,
      });
    }
  });

  const attribute_breakdowns = summary.fields.reduce((acc, field) => {
    acc[field.field_name] = {
      populated_items: field.populated_items,
      populated_pct: field.populated_pct,
      unclassified_items: field.unclassified_items,
      unclassified_pct: field.unclassified_pct,
      distinct_values: field.distinct_values,
      dominance_pct: field.dominance_pct,
      top_values: field.top_values,
    };
    return acc;
  }, {});

  return {
    sample_size: summary.sample_size,
    attribute_breakdowns,
    observations: observations.slice(0, 12),
  };
};

const buildWorkItemSignals = ({ sampleItems = [], fieldUsage = [], totalTasks = 0 }) => {
  const sample_summary = summarizeSampleItemAttributes(sampleItems);
  const field_usage_summary = extractFieldUsageInsights(fieldUsage, totalTasks, sampleItems);
  return {
    sample_item_summary: sample_summary,
    field_usage_summary,
    category_breakdowns: sample_summary.attribute_breakdowns,
    sample_item_observations: sample_summary.observations,
  };
};

const sumShownVariantTaskCoverage = (variantsJSON) => {
  if (!Array.isArray(variantsJSON)) return null;
  let total = 0;
  let found = false;

  variantsJSON.forEach((row) => {
    const pct = variantPercent(row);

    if (pct !== null) {
      total += pct;
      found = true;
    }
  });

  return found ? normalisePct(total) : null;
};

const buildDataCoverage = ({ totalTasks, totalVariants, transitionsJSON, variantsJSON, sampleItems, fieldUsage, statusTypeSummary, statusTypeMappings, activityContext = null }) => {
  const explicitStatusCount =
    toNumber(statusTypeSummary?.total_statuses) ??
    (Array.isArray(statusTypeMappings) ? statusTypeMappings.length : 0);
  const lifecycleStatusCount = (activityContext?.activity_kind === 'lifecycle_status' || isLifecycleStatusFieldName(activityContext?.activity_field || activityContext?.activity_label))
    ? uniqueLifecycleValuesFromTransitions(transitionsJSON).length
    : 0;

  return {
    total_tasks: totalTasks,
    transitions_count: Array.isArray(transitionsJSON) ? transitionsJSON.length : 0,
    variants_total: totalVariants,
    variants_shown: Array.isArray(variantsJSON) ? variantsJSON.length : 0,
    variants_shown_pct_of_total:
      totalVariants > 0 && Array.isArray(variantsJSON)
        ? normalisePct((variantsJSON.length / totalVariants) * 100)
        : null,
    shown_variant_task_coverage_percent: sumShownVariantTaskCoverage(variantsJSON),
    sample_size: Array.isArray(sampleItems) ? sampleItems.length : 0,
    sample_percent:
      totalTasks > 0 && Array.isArray(sampleItems)
        ? normalisePct((sampleItems.length / totalTasks) * 100)
        : null,
    field_usage_rows: Array.isArray(fieldUsage) ? fieldUsage.length : 0,
    status_types_count: explicitStatusCount > 0 ? explicitStatusCount : lifecycleStatusCount,
  };
};

const METRIC_KEYS = [
  'cycle_time',
  'std_deviation',
  'rework_rate',
  'avg_transitions',
  'avg_cycle_time_started',
  'avg_resolution_time',
  'avg_touch_time',
  'avg_wait_time',
  'avg_admin_time',
  'avg_queue_time',
  'avg_flow_efficiency',
  'avg_time_spent',
  'avg_customer_comments',
  'avg_internal_comments',
  'avg_worklogs',
  'compliance',
  'workflow_alignment',
];

const isMetricValid = (row, key) => {
  if (!row || typeof row !== 'object') return true;
  if (typeof row[`${key}_valid`] === 'boolean') return row[`${key}_valid`];
  if (row.metric_validity && typeof row.metric_validity[key] === 'boolean') return row.metric_validity[key];
  return true;
};

const cleanMetricRow = (row) => {
  if (!row || typeof row !== 'object') return row;
  const cleaned = { ...row };

  for (const key of METRIC_KEYS) {
    if (!isMetricValid(row, key)) cleaned[key] = null;
  }

  return cleaned;
};

const pickMetric = (row, key) => {
  if (!row || !isMetricValid(row, key)) return null;
  return toNumber(row[key]);
};

const buildProcessIntelligence = (metricsHistory) => {
  const row = cleanMetricRow(firstMetricRow(metricsHistory));

  const payload = {
    work_items: extractTotalTasks(metricsHistory, 0),
    variants: extractTotalVariants(metricsHistory, 0),
    cycle_time: pickMetric(row, 'cycle_time'),
    std_deviation: pickMetric(row, 'std_deviation'),
    rework_rate: pickMetric(row, 'rework_rate'),
    avg_transitions: pickMetric(row, 'avg_transitions'),
    avg_cycle_time_started: pickMetric(row, 'avg_cycle_time_started'),
    avg_resolution_time: pickMetric(row, 'avg_resolution_time'),
    avg_touch_time: pickMetric(row, 'avg_touch_time'),
    avg_wait_time: pickMetric(row, 'avg_wait_time'),
    avg_admin_time: pickMetric(row, 'avg_admin_time'),
    avg_queue_time: pickMetric(row, 'avg_queue_time'),
    avg_flow_efficiency: pickMetric(row, 'avg_flow_efficiency'),
    avg_time_spent: pickMetric(row, 'avg_time_spent'),
    avg_customer_comments: pickMetric(row, 'avg_customer_comments'),
    avg_internal_comments: pickMetric(row, 'avg_internal_comments'),
    avg_worklogs: pickMetric(row, 'avg_worklogs'),
    validity: row.metric_validity || null,
  };

  const substantiveKeys = [
    'cycle_time',
    'std_deviation',
    'rework_rate',
    'avg_transitions',
    'avg_cycle_time_started',
    'avg_resolution_time',
    'avg_touch_time',
    'avg_wait_time',
    'avg_admin_time',
    'avg_queue_time',
    'avg_flow_efficiency',
    'avg_time_spent',
    'avg_customer_comments',
    'avg_internal_comments',
    'avg_worklogs',
  ];

  return substantiveKeys.some((key) => hasMeaningfulValue(payload[key])) ? payload : null;
};

const buildStatusTypeDataset = ({ statusTypeSummary, statusTypeMappings, transitionsJSON, activityContext = null }) => {
  const explicitMappings = Array.isArray(statusTypeMappings)
    ? statusTypeMappings.map((item) => ({
        activity: item.activity || item.statusName || item.status || null,
        status: item.statusName || item.status || item.activity || null,
        operational_type: item.operationalType || item.operational_type || null,
        time_bucket: item.timeBucket || item.time_bucket || null,
        confidence: item.confidence ?? null,
        match_source: item.matchSource || item.match_source || null,
        reason: item.reason || null,
      })).filter((item) => item.activity || item.status)
    : [];

  const isLifecycle = activityContext?.activity_kind === 'lifecycle_status' || isLifecycleStatusFieldName(activityContext?.activity_field || activityContext?.activity_label);
  const derivedMappings = (!explicitMappings.length && isLifecycle)
    ? uniqueLifecycleValuesFromTransitions(transitionsJSON).map(classifyLifecycleStatusValue)
    : [];

  const mappings = explicitMappings.length ? explicitMappings : derivedMappings;

  const transitionProfile = {
    by_transition_type: {},
    by_from_time_bucket: {},
    by_to_time_bucket: {},
  };

  (Array.isArray(transitionsJSON) ? transitionsJSON : []).forEach((row) => {
    const count =
      toNumber(row['Work items']) ??
      toNumber(row['Work Items']) ??
      toNumber(row['Tasks']) ??
      toNumber(row['Count']) ??
      1;

    const tt = row.transition_type || row['transition_type'];
    const fromBucket = row.from_time_bucket || row['from_time_bucket'];
    const toBucket = row.to_time_bucket || row['to_time_bucket'];

    if (tt) transitionProfile.by_transition_type[tt] = (transitionProfile.by_transition_type[tt] || 0) + count;
    if (fromBucket) transitionProfile.by_from_time_bucket[fromBucket] = (transitionProfile.by_from_time_bucket[fromBucket] || 0) + count;
    if (toBucket) transitionProfile.by_to_time_bucket[toBucket] = (transitionProfile.by_to_time_bucket[toBucket] || 0) + count;
  });

  const summary = statusTypeSummary && (statusTypeSummary.total_statuses || statusTypeSummary.total_activities)
    ? {
        total_statuses: statusTypeSummary.total_statuses ?? mappings.length,
        total_activities: statusTypeSummary.total_activities ?? statusTypeSummary.total_statuses ?? mappings.length,
        activity_kind: activityContext?.activity_kind || null,
        activity_field: activityContext?.activity_field || null,
        activity_label: activityContext?.activity_label || null,
        by_operational_type: statusTypeSummary.by_operational_type || countBy(mappings, 'operational_type'),
        by_time_bucket: statusTypeSummary.by_time_bucket || countBy(mappings, 'time_bucket'),
        derived_from_activity_field: !explicitMappings.length && derivedMappings.length > 0,
      }
    : {
        total_statuses: mappings.length,
        total_activities: mappings.length,
        activity_kind: activityContext?.activity_kind || null,
        activity_field: activityContext?.activity_field || null,
        activity_label: activityContext?.activity_label || null,
        by_operational_type: countBy(mappings, 'operational_type'),
        by_time_bucket: countBy(mappings, 'time_bucket'),
        derived_from_activity_field: !explicitMappings.length && derivedMappings.length > 0,
      };

  const payload = {
    summary,
    mappings,
  };

  if (Object.values(transitionProfile).some((bucket) => Object.keys(bucket).length > 0)) {
    payload.transition_profile = transitionProfile;
  }

  return payload;
};

export const normalizeConsultantPromptData = ({
  metricsHistory = [],
  metricsTotal = null,
  transitionsJSON = [],
  variantsJSON = [],
  sampleItems = [],
  fieldUsage = [],
  statusTypeSummary = null,
  statusTypeMappings = [],
  activityContext = null,
  activityType = null,
  activityField = null,
  activityLabel = null,
}) => {
  // Prefer the model-wide metricsTotal row for totals. metricsHistory[0] can be
  // a single period/bucket row, which is why Quick HealthCheck could say things
  // like "only 6 incidents" even when the model contains many more items.
  const totalSourceRows = metricsTotal ? [cleanMetricRow(metricsTotal)] : metricsHistory;
  const sampleFallback = Array.isArray(sampleItems) ? sampleItems.length : 0;
  const variantFallback = Array.isArray(variantsJSON) ? variantsJSON.length : 0;
  const totalTasks = extractTotalTasks(totalSourceRows, sampleFallback);
  const totalVariants = extractTotalVariants(totalSourceRows, variantFallback);

  const normalizedMetrics = Array.isArray(metricsHistory)
    ? metricsHistory.map((row, idx) => {
        const cleaned = cleanMetricRow(row);
        if (idx !== 0) return cleaned;

        return {
          ...cleaned,
          total_tasks: totalTasks,
          total_variants: totalVariants,
          variants_shown: Array.isArray(variantsJSON) ? variantsJSON.length : 0,
          task_sample_size: Array.isArray(sampleItems) ? sampleItems.length : 0,
          task_sample_percent:
            totalTasks > 0 && Array.isArray(sampleItems)
              ? normalisePct((sampleItems.length / totalTasks) * 100)
              : null,
        };
      })
    : [];

  const normalizedTransitions = Array.isArray(transitionsJSON)
    ? transitionsJSON.map((row) => {
        const workItems = transitionVolume(row);
        const explicitPct = transitionPercent(row);
        const pct = explicitPct !== null
          ? explicitPct
          : (totalTasks > 0 && workItems > 0 ? normalisePct((workItems / totalTasks) * 100) : null);

        const cleaned = { ...row };
        delete cleaned['% total Tasks'];

        return {
          ...cleaned,
          pct_total_tasks: pct,
          pct_total_items: pct,
        };
      })
    : [];

  const normalizedVariants = {
    total_tasks: totalTasks,
    variants_total: totalVariants,
    variants_shown: Array.isArray(variantsJSON) ? variantsJSON.length : 0,
    coverage_percent:
      totalVariants > 0 && Array.isArray(variantsJSON)
        ? normalisePct((variantsJSON.length / totalVariants) * 100)
        : null,
    rows: Array.isArray(variantsJSON) ? variantsJSON : [],
  };

  const normalizedSampleItems = {
    total_tasks: totalTasks,
    sample_size: Array.isArray(sampleItems) ? sampleItems.length : 0,
    sample_percent:
      totalTasks > 0 && Array.isArray(sampleItems)
        ? normalisePct((sampleItems.length / totalTasks) * 100)
        : null,
    rows: Array.isArray(sampleItems) ? sampleItems : [],
  };

  const normalizedFieldUsage = {
    total_tasks: totalTasks,
    fields: Array.isArray(fieldUsage) ? fieldUsage : [],
  };

  const normalizedActivityContext = buildActivityContext({ activityContext, activityType, activityField, activityLabel });

  const normalizedStatusTypes = buildStatusTypeDataset({
    statusTypeSummary,
    statusTypeMappings,
    transitionsJSON: normalizedTransitions,
    activityContext: normalizedActivityContext,
  });

  // Prefer the model-wide total row for process intelligence when the caller
  // has supplied one. The per-period metricsHistory[0] is just a single period
  // and produces unrepresentative scores when that period is short or sparse.
  // Fallback to metricsHistory-based inference preserves legacy behaviour.
  const processIntelligenceSource = metricsTotal
    ? [cleanMetricRow(metricsTotal)]
    : normalizedMetrics;
  const processIntelligence = buildProcessIntelligence(processIntelligenceSource);

  const dataCoverage = buildDataCoverage({
    totalTasks,
    totalVariants,
    transitionsJSON,
    variantsJSON,
    sampleItems,
    fieldUsage,
    statusTypeSummary,
    statusTypeMappings,
    activityContext,
    activityType,
    activityField,
    activityLabel,
  });

  return {
    metricsHistory: normalizedMetrics,
    transitionsJSON: normalizedTransitions,
    variantsJSON: normalizedVariants,
    sampleItems: normalizedSampleItems,
    fieldUsage: normalizedFieldUsage,
    statusTypes: normalizedStatusTypes,
    activityContext: normalizedActivityContext,
    processIntelligence,
    dataCoverage,
  };
};



export const buildImprovementOpportunitiesInputEvidence = ({
  issueTypeStr = '',
  metricsHistory = [],
  metricsTotal = null,
  transitionsJSON = [],
  variantsJSON = [],
  sampleItems = [],
  fieldUsage = [],
  statusTypeSummary = null,
  statusTypeMappings = [],
  activityContext = null,
  activityType = null,
  activityField = null,
  activityLabel = null,
  hourlyRate = 90,
}) => {
  const normalized = normalizeConsultantPromptData({
    metricsHistory,
    metricsTotal,
    transitionsJSON,
    variantsJSON,
    sampleItems,
    fieldUsage,
    statusTypeSummary,
    statusTypeMappings,
    activityContext,
    activityType,
    activityField,
    activityLabel,
  });

  const metricRow = metricsTotal
    ? cleanMetricRow(metricsTotal)
    : (Array.isArray(normalized.metricsHistory) && normalized.metricsHistory.length > 0
      ? normalized.metricsHistory[0]
      : {});

  const firstNonNull = (...vals) => {
    for (const v of vals) if (v !== null && v !== undefined) return v;
    return null;
  };

  const processSummary = {
    total_items: firstNonNull(
      toNumber(metricRow.total_tasks),
      toNumber(metricRow.total_items),
      toNumber(metricRow.work_items),
      toNumber(metricRow.items),
      toNumber(metricRow.tasks),
      toNumber(metricRow['Tasks']),
      toNumber(metricRow['Work Items']),
      toNumber(metricRow['Work items']),
      normalized.dataCoverage?.total_tasks
    ),
    avg_duration_hours: firstNonNull(
      hoursValue(metricRow.avg_duration_hours),
      hoursValue(metricRow.avg_hours),
      hoursValue(metricRow['Avg Duration']),
      hoursValue(metricRow['Avg Duration (hrs)']),
      secondsToHoursStrict(metricRow.cycle_time)
    ),
    std_deviation_hours: firstNonNull(
      hoursValue(metricRow.std_deviation_hours),
      hoursValue(metricRow.std_hours),
      hoursValue(metricRow['Std Deviation']),
      hoursValue(metricRow['Std Deviation (hrs)']),
      secondsToHoursStrict(metricRow.std_deviation)
    ),
    rework_rate_percent: firstNonNull(
      normalizePercentValue(metricRow.rework_rate),
      normalizePercentValue(metricRow.rework_pct),
      normalizePercentValue(metricRow['% Rework'])
    ),
    avg_transitions: firstNonNull(
      toNumber(metricRow.avg_transitions),
      toNumber(metricRow['Avg Transitions'])
    ),
    variants_total: firstNonNull(
      toNumber(metricRow.total_variants),
      toNumber(metricRow.variants),
      toNumber(metricRow.Variants),
      normalized.variantsJSON?.variants_total
    ),
    activities: firstNonNull(toNumber(metricRow.Activities), toNumber(metricRow.activities)),
    steps: firstNonNull(toNumber(metricRow.Steps), toNumber(metricRow.steps)),
  };

  const pi = normalized.processIntelligence || {};
  const validity = pi.validity || {};
  // Upstream values are in seconds; field names end in _hours so we must convert.
  const secondsToHours = (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number((Number(v) / 3600).toFixed(2)));
  const timeIntelligence = {
    avg_cycle_time_hours: secondsToHours(firstNonNull(pi.avg_cycle_time_started, pi.cycle_time)),
    avg_resolution_time_hours: secondsToHours(pi.avg_resolution_time),
    avg_touch_time_hours: secondsToHours(pi.avg_touch_time),
    avg_wait_time_hours: secondsToHours(pi.avg_wait_time),
    avg_admin_time_hours: secondsToHours(pi.avg_admin_time),
    avg_queue_time_hours: secondsToHours(pi.avg_queue_time),
    flow_efficiency_percent:
      pi.avg_flow_efficiency !== null && pi.avg_flow_efficiency !== undefined && Number.isFinite(Number(pi.avg_flow_efficiency))
        ? Number((Number(pi.avg_flow_efficiency) * 100).toFixed(2))
        : null,
    validity: {
      avg_cycle_time_hours: validity.avg_cycle_time_started ?? validity.cycle_time ?? (pi.avg_cycle_time_started != null || pi.cycle_time != null),
      avg_resolution_time_hours: validity.avg_resolution_time ?? (pi.avg_resolution_time != null),
      avg_touch_time_hours: validity.avg_touch_time ?? (pi.avg_touch_time != null),
      avg_wait_time_hours: validity.avg_wait_time ?? (pi.avg_wait_time != null),
      avg_admin_time_hours: validity.avg_admin_time ?? (pi.avg_admin_time != null),
      avg_queue_time_hours: validity.avg_queue_time ?? (pi.avg_queue_time != null),
      flow_efficiency_percent: validity.avg_flow_efficiency ?? (pi.avg_flow_efficiency != null),
    },
  };

  const effectiveTimeIntelligence = contextualizeTimeIntelligence(timeIntelligence, normalized.activityContext);

  const sortByDesc = (rows, getter) => [...(Array.isArray(rows) ? rows : [])].sort((a, b) => (getter(b) ?? -Infinity) - (getter(a) ?? -Infinity));

  const volumeOfTransition = transitionVolume;
  const delayOfTransition = transitionAvgHours;
  const pctOfTransition = transitionPercent;

  const allTransitions = Array.isArray(normalized.transitionsJSON) ? normalized.transitionsJSON : [];

  // Use model-level total_items as the source of truth for transition percentages.
  // Some grid columns expose percentage values relative to a selected/visible subset,
  // which made low-volume delay transitions appear as 13% instead of 0.13%.
  const pctOfTransitionAgainstModel = (r) => {
    const count = volumeOfTransition(r);
    const total = toNumber(processSummary.total_items);
    if (total && total > 0 && count !== null && count !== undefined) {
      return normalisePct((Number(count) / total) * 100);
    }
    return pctOfTransition(r);
  };

  const mapTransition = (r) => {
    const transition = transitionNameFromRow(r);
    const split = splitTransitionName(transition);
    const from = firstPresent(r['From Activity'], r.from_activity, r.from, split.from);
    const to = firstPresent(r['To Activity'], r.to_activity, r.to, split.to);
    const workItems = volumeOfTransition(r);
    return {
      transition,
      from: from || null,
      to: to || null,
      work_items: workItems,
      pct_total_items: pctOfTransitionAgainstModel(r),
      avg_duration_hours: delayOfTransition(r),
      std_deviation_hours: transitionStdHours(r),
      is_rework: isReworkRow(r),
      transition_type: r.transition_type || r['transition_type'] || null,
      from_time_bucket: r.from_time_bucket || r['from_time_bucket'] || null,
      to_time_bucket: r.to_time_bucket || r['to_time_bucket'] || null,
    };
  };

  const topTransitions = sortByDesc(allTransitions, volumeOfTransition).slice(0, 10).map(mapTransition);

  const topDelayTransitions = sortByDesc(allTransitions, (r) => delayOfTransition(r) ?? -Infinity).slice(0, 10).map(mapTransition);

  const reworkTransitions = allTransitions.filter(isReworkRow);

  const topReworkTransitions = sortByDesc(reworkTransitions, volumeOfTransition).slice(0, 10).map(mapTransition);

  const allVariants = Array.isArray(normalized.variantsJSON?.rows) ? normalized.variantsJSON.rows : [];
  const pctOfVariant = variantPercent;
  const loopsOfVariant = variantLoops;
  const durOfVariant = variantAvgHours;
  const stdOfVariant = variantStdHours;

  const topVariants = sortByDesc(allVariants, (r) => variantVolume(r) ?? pctOfVariant(r) ?? 0).slice(0, 12).map((r) => ({
    path_name: variantNameFromRow(r),
    variant_id: variantIdFromRow(r),
    work_items: variantVolume(r),
    pct_total_items: pctOfVariant(r),
    avg_duration_hours: durOfVariant(r),
    std_deviation_hours: stdOfVariant(r),
    loops: loopsOfVariant(r),
    is_rework: isReworkRow(r) || loopsOfVariant(r) > 0,
  }));

  const reworkVariants = topVariants.filter((r) => r.is_rework).slice(0, 6);

  const workItemSignals = buildWorkItemSignals({
    sampleItems: Array.isArray(normalized.sampleItems?.rows) ? normalized.sampleItems.rows : [],
    fieldUsage: Array.isArray(normalized.fieldUsage?.fields) ? normalized.fieldUsage.fields : [],
    totalTasks: processSummary.total_items ?? normalized.dataCoverage?.total_tasks ?? 0,
  });

  const workloadSignals = {
    // Keep the shared AI payload concise and consistent across all Advisor AI content.
    // Attribute usage is the single source for workload / segmentation context.
    // Sparse, highly-used, classification, and breakdown-style signals can be derived
    // from population_rate, distinct_values, dominance_pct, classification_strength,
    // and top_values.
    attribute_usage: workItemSignals.field_usage_summary.field_usage_highlights || [],
  };

  const slaSignals = buildSlaSignals({
    sampleItems: Array.isArray(normalized.sampleItems?.rows) ? normalized.sampleItems.rows : [],
    fieldUsage: Array.isArray(normalized.fieldUsage?.fields) ? normalized.fieldUsage.fields : [],
    totalTasks: processSummary.total_items ?? normalized.dataCoverage?.total_tasks ?? 0,
  });

  const { activityModel, statusModel, isLifecycle } = buildActivityAndStatusModels(normalized, normalized.activityContext);

  const sampleSize = normalized.sampleItems?.sample_size ?? 0;
  const fieldRows = Array.isArray(normalized.fieldUsage?.fields) ? normalized.fieldUsage.fields : [];

  const dataCoverage = {
    process_metrics: !!(processSummary.avg_duration_hours != null || processSummary.rework_rate_percent != null || processSummary.variants_total != null),
    transitions: topTransitions.length > 0,
    variants: topVariants.length > 0,
    field_usage: fieldRows.length > 0 || sampleSize > 0,
    activity_model: (activityModel.activities || []).length > 0,
    status_types: isLifecycle && (statusModel?.statuses || []).length > 0,
    time_intelligence: Object.values(effectiveTimeIntelligence.validity || {}).some(Boolean),
    sample_items: sampleSize > 0,
    sla_metrics: slaSignals.available === true,
    evidence_base_label: [
      processSummary.avg_duration_hours != null || processSummary.rework_rate_percent != null ? 'metrics' : null,
      topTransitions.length ? 'transitions' : null,
      topVariants.length ? 'variants' : null,
      fieldRows.length ? 'field usage' : null,
      sampleSize > 0 ? 'sample items' : null,
      (activityModel.activities || []).length ? 'activity model' : null,
      Object.values(effectiveTimeIntelligence.validity || {}).some(Boolean) ? 'time intelligence' : null,
      slaSignals.available ? 'task_sla metrics' : null,
    ].filter(Boolean).join(', ') || 'limited evidence',
    notes: [
      sampleSize > 0 ? `Sample item evidence available for ${sampleSize} items` : null,
    ].filter(Boolean),
  };

  return {
    context: {
      workflow_name: issueTypeStr || '',
      entity_name: 'work items',
      total_items: processSummary.total_items ?? normalized.dataCoverage?.total_tasks ?? null,
    },
    process_summary: processSummary,
    time_intelligence: effectiveTimeIntelligence,
    activity_context: normalized.activityContext,
    activity_model: activityModel,
    status_model: statusModel,
    transition_signals: {
      top_transitions: topTransitions,
      top_delay_transitions: topDelayTransitions,
      top_rework_transitions: topReworkTransitions,
    },
    roi_estimate: calculateDirectionalROI({
      processSummary,
      timeIntelligence: effectiveTimeIntelligence,
      transitionSignals: {
        top_transitions: topTransitions,
        top_delay_transitions: topDelayTransitions,
        top_rework_transitions: topReworkTransitions,
      },
      hourlyRate,
    }),
    variant_signals: {
      top_variants: topVariants,
      rework_variants: reworkVariants,
      long_tail_summary: {
        variants_total: normalized.variantsJSON?.variants_total ?? null,
        variants_shown: normalized.variantsJSON?.variants_shown ?? null,
        variants_shown_pct_of_total: normalized.dataCoverage?.variants_shown_pct_of_total ?? null,
        shown_variant_task_coverage_percent: normalized.dataCoverage?.shown_variant_task_coverage_percent ?? null,
      },
    },
    workload_signals: workloadSignals,
    sla_signals: slaSignals,
    data_coverage: dataCoverage,
  };
};


export const buildWorkflowRedesignInputEvidence = ({
  issueTypeStr = '',
  metricsHistory = [],
  metricsTotal = null,
  transitionsJSON = [],
  variantsJSON = [],
  sampleItems = [],
  fieldUsage = [],
  statusTypeSummary = null,
  statusTypeMappings = [],
  activityContext = null,
  activityType = null,
  activityField = null,
  activityLabel = null,
}) => {
  const normalized = normalizeConsultantPromptData({
    metricsHistory,
    metricsTotal,
    transitionsJSON,
    variantsJSON,
    sampleItems,
    fieldUsage,
    statusTypeSummary,
    statusTypeMappings,
    activityContext,
    activityType,
    activityField,
    activityLabel,
  });

  const metricRow = metricsTotal
    ? cleanMetricRow(metricsTotal)
    : (Array.isArray(normalized.metricsHistory) && normalized.metricsHistory.length > 0
      ? normalized.metricsHistory[0]
      : {});

  const firstNonNull = (...vals) => {
    for (const v of vals) if (v !== null && v !== undefined) return v;
    return null;
  };

  const formatHours = (num) => { /* seconds -> hours */
    if (num === null || num === undefined || !Number.isFinite(Number(num))) return null;
    return Number((Number(num) / 3600).toFixed(2));
  };

  const processSummary = {
    total_items: firstNonNull(
      toNumber(metricRow.total_tasks),
      toNumber(metricRow['Tasks']),
      toNumber(metricRow['Work Items']),
      toNumber(metricRow['Work items'])
    ),
    avg_duration_hours: firstNonNull(
      toNumber(metricRow.cycle_time),
      toNumber(metricRow['Avg Duration']),
      toNumber(metricRow['Avg Duration (hrs)'])
    ),
    std_deviation_hours: firstNonNull(
      toNumber(metricRow.std_deviation),
      toNumber(metricRow['Std Deviation']),
      toNumber(metricRow['Std Deviation (hrs)'])
    ),
    rework_rate_percent: firstNonNull(
      toNumber(metricRow.rework_rate),
      toNumber(metricRow['% Rework'])
    ),
    avg_transitions: firstNonNull(
      toNumber(metricRow.avg_transitions),
      toNumber(metricRow['Avg Transitions'])
    ),
    variants_total: firstNonNull(
      toNumber(metricRow.total_variants),
      normalized.variantsJSON?.variants_total
    ),
    activities: firstNonNull(
      toNumber(metricRow.Activities),
      toNumber(metricRow.activities)
    ),
    steps: firstNonNull(
      toNumber(metricRow.Steps),
      toNumber(metricRow.steps)
    ),
  };

  const pi = normalized.processIntelligence || {};
  const validity = pi.validity || {};
  const timeIntelligence = {
    avg_cycle_time_hours: formatHours(firstNonNull(pi.avg_cycle_time_started, pi.cycle_time)),
    avg_resolution_time_hours: formatHours(pi.avg_resolution_time),
    avg_touch_time_hours: formatHours(pi.avg_touch_time),
    avg_wait_time_hours: formatHours(pi.avg_wait_time),
    avg_admin_time_hours: formatHours(pi.avg_admin_time),
    avg_queue_time_hours: formatHours(pi.avg_queue_time),
    flow_efficiency_percent:
      pi.avg_flow_efficiency !== null && pi.avg_flow_efficiency !== undefined && Number.isFinite(Number(pi.avg_flow_efficiency))
        ? Number((Number(pi.avg_flow_efficiency) * 100).toFixed(2))
        : null,
    validity: {
      avg_cycle_time_hours: validity.avg_cycle_time_started ?? validity.cycle_time ?? (pi.avg_cycle_time_started != null || pi.cycle_time != null),
      avg_resolution_time_hours: validity.avg_resolution_time ?? (pi.avg_resolution_time != null),
      avg_touch_time_hours: validity.avg_touch_time ?? (pi.avg_touch_time != null),
      avg_wait_time_hours: validity.avg_wait_time ?? (pi.avg_wait_time != null),
      avg_admin_time_hours: validity.avg_admin_time ?? (pi.avg_admin_time != null),
      avg_queue_time_hours: validity.avg_queue_time ?? (pi.avg_queue_time != null),
      flow_efficiency_percent: validity.avg_flow_efficiency ?? (pi.avg_flow_efficiency != null),
    },
  };

  const effectiveTimeIntelligence = contextualizeTimeIntelligence(timeIntelligence, normalized.activityContext);

  const sortByDesc = (rows, getter) => [...(Array.isArray(rows) ? rows : [])].sort((a, b) => (getter(b) ?? -Infinity) - (getter(a) ?? -Infinity));
  const volumeOfTransition = (r) =>
    toNumber(r['Work items']) ?? toNumber(r['Work Items']) ?? toNumber(r.Tasks) ?? toNumber(r.Count) ?? 0;
  const delayOfTransition = (r) =>
    toNumber(r['Avg deviation (hrs)']) ?? toNumber(r['Avg Duration (hrs)']) ?? toNumber(r.avg_duration_hrs) ?? 0;
  const pctOfTransition = (r) =>
    toNumber(r.pct_total_tasks) ?? toNumber(r['% total Tasks']) ?? toNumber(r['% total work items']) ?? null;

  const allTransitions = Array.isArray(normalized.transitionsJSON) ? normalized.transitionsJSON : [];
  const topTransitions = sortByDesc(allTransitions, volumeOfTransition).slice(0, 12).map((r) => ({
    transition: r.Transition || r.transition || null,
    from: r['From Activity'] || r.from_activity || null,
    to: r['To Activity'] || r.to_activity || null,
    work_items: volumeOfTransition(r),
    pct_total_items: pctOfTransition(r),
    avg_duration_hours: delayOfTransition(r),
    std_deviation_hours: toNumber(r['Std deviation (hrs)']) ?? toNumber(r['Std Deviation (hrs)']) ?? toNumber(r.std_deviation_hrs),
    is_rework: r['Is rework?'] === true || r['Is Rework'] === true || r.loop === true,
    transition_type: r.transition_type || null,
    from_time_bucket: r.from_time_bucket || null,
    to_time_bucket: r.to_time_bucket || null,
  }));

  const topDelayTransitions = sortByDesc(allTransitions, delayOfTransition).slice(0, 12).map((r) => ({
    transition: r.Transition || r.transition || null,
    from: r['From Activity'] || r.from_activity || null,
    to: r['To Activity'] || r.to_activity || null,
    work_items: volumeOfTransition(r),
    pct_total_items: pctOfTransition(r),
    avg_duration_hours: delayOfTransition(r),
    is_rework: r['Is rework?'] === true || r['Is Rework'] === true || r.loop === true,
    transition_type: r.transition_type || null,
    from_time_bucket: r.from_time_bucket || null,
    to_time_bucket: r.to_time_bucket || null,
  }));

  const reworkTransitions = allTransitions.filter((r) => r['Is rework?'] === true || r['Is Rework'] === true || r.loop === true);
  const topReworkTransitions = sortByDesc(reworkTransitions, volumeOfTransition).slice(0, 10).map((r) => ({
    transition: r.Transition || r.transition || null,
    from: r['From Activity'] || r.from_activity || null,
    to: r['To Activity'] || r.to_activity || null,
    work_items: volumeOfTransition(r),
    pct_total_items: pctOfTransition(r),
    avg_duration_hours: delayOfTransition(r),
    transition_type: r.transition_type || null,
  }));

  const allVariants = Array.isArray(normalized.variantsJSON?.rows) ? normalized.variantsJSON.rows : [];
  const pctOfVariant = (r) => toNumber(r['% Total Tasks']) ?? toNumber(r['% total Tasks']) ?? toNumber(r.pct_total_tasks) ?? 0;
  const loopsOfVariant = (r) => toNumber(r.Loops) ?? 0;
  const durOfVariant = (r) => toNumber(r['Avg Duration (hrs)']) ?? toNumber(r.avg_duration_hrs) ?? 0;
  const stdOfVariant = (r) => toNumber(r['Std Deviation (hrs)']) ?? toNumber(r.std_deviation_hrs) ?? 0;

  const topVariants = sortByDesc(allVariants, pctOfVariant).slice(0, 8).map((r) => ({
    path_name: r.Variant || r.variant || null,
    variant_id: r.ID || r.variant_id || null,
    work_items: toNumber(r.Tasks) ?? toNumber(r['Tasks']) ?? toNumber(r['Work Items']) ?? null,
    pct_total_items: pctOfVariant(r),
    avg_duration_hours: durOfVariant(r),
    std_deviation_hours: stdOfVariant(r),
    loops: loopsOfVariant(r),
    is_rework: r['Is Rework'] === true || r.loop === true || loopsOfVariant(r) > 0,
  }));

  const reworkVariants = topVariants.filter((r) => r.is_rework).slice(0, 6);

  const fieldRows = Array.isArray(normalized.fieldUsage?.fields) ? normalized.fieldUsage.fields : [];
  const sampleRows = Array.isArray(normalized.sampleItems?.rows) ? normalized.sampleItems.rows : [];
  const workItemSignals = buildWorkItemSignals({
    sampleItems: sampleRows,
    fieldUsage: fieldRows,
    totalTasks: processSummary.total_items ?? normalized.dataCoverage?.total_tasks ?? 0,
  });

  const workloadSignals = {
    // Keep the shared AI payload concise and consistent across all Advisor AI content.
    // Attribute usage is the single source for workload / segmentation context.
    // Sparse, highly-used, classification, and breakdown-style signals can be derived
    // from population_rate, distinct_values, dominance_pct, classification_strength,
    // and top_values.
    attribute_usage: workItemSignals.field_usage_summary.field_usage_highlights || [],
  };

  const slaSignals = buildSlaSignals({
    sampleItems: sampleRows,
    fieldUsage: fieldRows,
    totalTasks: processSummary.total_items ?? normalized.dataCoverage?.total_tasks ?? 0,
  });

  const { activityModel, statusModel, isLifecycle } = buildActivityAndStatusModels(normalized, normalized.activityContext);

  const dataCoverage = {
    process_metrics: !!(processSummary.avg_duration_hours != null || processSummary.rework_rate_percent != null || processSummary.variants_total != null),
    transitions: topTransitions.length > 0,
    variants: topVariants.length > 0,
    field_usage: fieldRows.length > 0 || sampleRows.length > 0,
    activity_model: (activityModel.activities || []).length > 0,
    status_types: isLifecycle && (statusModel?.statuses || []).length > 0,
    time_intelligence: Object.values(effectiveTimeIntelligence.validity || {}).some(Boolean),
    sla_metrics: slaSignals.available === true,
    evidence_base_label: [
      processSummary.avg_duration_hours != null || processSummary.rework_rate_percent != null ? 'metrics' : null,
      topTransitions.length ? 'transitions' : null,
      topVariants.length ? 'variants' : null,
      fieldRows.length ? 'field usage' : null,
      sampleRows.length ? 'sample items' : null,
      (activityModel.activities || []).length ? 'activity model' : null,
      Object.values(effectiveTimeIntelligence.validity || {}).some(Boolean) ? 'time intelligence' : null,
      slaSignals.available ? 'task_sla metrics' : null,
    ].filter(Boolean).join(', ') || 'limited evidence',
    notes: [
      sampleRows.length > 0 ? `Sample item evidence available for ${sampleRows.length} items` : null,
    ].filter(Boolean),
  };

  return {
    context: {
      workflow_name: issueTypeStr || '',
      entity_name: 'work items',
      total_items: processSummary.total_items ?? normalized.dataCoverage?.total_tasks ?? null,
    },
    process_summary: processSummary,
    time_intelligence: effectiveTimeIntelligence,
    activity_context: normalized.activityContext,
    activity_model: activityModel,
    status_model: statusModel,
    transition_signals: {
      top_transitions: topTransitions,
      top_delay_transitions: topDelayTransitions,
      top_rework_transitions: topReworkTransitions,
    },
    variant_signals: {
      top_variants: topVariants,
      rework_variants: reworkVariants,
      long_tail_summary: {
        variants_total: normalized.variantsJSON?.variants_total ?? null,
        variants_shown: normalized.variantsJSON?.variants_shown ?? null,
        variants_shown_pct_of_total: normalized.dataCoverage?.variants_shown_pct_of_total ?? null,
        shown_variant_task_coverage_percent: normalized.dataCoverage?.shown_variant_task_coverage_percent ?? null,
      },
    },
    workload_signals: workloadSignals,
    sla_signals: slaSignals,
    data_coverage: dataCoverage,
  };
};

export const buildAutomationBlueprintInputEvidence = ({
  issueTypeStr = '',
  metricsHistory = [],
  metricsTotal = null,
  transitionsJSON = [],
  variantsJSON = [],
  sampleItems = [],
  fieldUsage = [],
  statusTypeSummary = null,
  statusTypeMappings = [],
  activityContext = null,
  activityType = null,
  activityField = null,
  activityLabel = null,
}) => {
  const normalized = normalizeConsultantPromptData({
    metricsHistory,
    metricsTotal,
    transitionsJSON,
    variantsJSON,
    sampleItems,
    fieldUsage,
    statusTypeSummary,
    statusTypeMappings,
    activityContext,
    activityType,
    activityField,
    activityLabel,
  });

  const metricRow = metricsTotal
    ? cleanMetricRow(metricsTotal)
    : (Array.isArray(normalized.metricsHistory) && normalized.metricsHistory.length > 0
      ? normalized.metricsHistory[0]
      : {});

  const firstNonNull = (...vals) => {
    for (const v of vals) if (v !== null && v !== undefined) return v;
    return null;
  };

  const formatHours = (num) => { /* seconds -> hours */
    if (num === null || num === undefined || !Number.isFinite(Number(num))) return null;
    return Number((Number(num) / 3600).toFixed(2));
  };

  const processSummary = {
    total_items: firstNonNull(
      toNumber(metricRow.total_tasks),
      toNumber(metricRow['Tasks']),
      toNumber(metricRow['Work Items']),
      toNumber(metricRow['Work items'])
    ),
    avg_duration_hours: firstNonNull(
      toNumber(metricRow.cycle_time),
      toNumber(metricRow['Avg Duration']),
      toNumber(metricRow['Avg Duration (hrs)'])
    ),
    std_deviation_hours: firstNonNull(
      toNumber(metricRow.std_deviation),
      toNumber(metricRow['Std Deviation']),
      toNumber(metricRow['Std Deviation (hrs)'])
    ),
    rework_rate_percent: firstNonNull(
      toNumber(metricRow.rework_rate),
      toNumber(metricRow['% Rework'])
    ),
    avg_transitions: firstNonNull(
      toNumber(metricRow.avg_transitions),
      toNumber(metricRow['Avg Transitions'])
    ),
    variants_total: firstNonNull(
      toNumber(metricRow.total_variants),
      normalized.variantsJSON?.variants_total
    ),
    activities: firstNonNull(toNumber(metricRow.Activities), toNumber(metricRow.activities)),
    steps: firstNonNull(toNumber(metricRow.Steps), toNumber(metricRow.steps)),
  };

  const pi = normalized.processIntelligence || {};
  const validity = pi.validity || {};
  const timeIntelligence = {
    avg_cycle_time_hours: formatHours(firstNonNull(pi.avg_cycle_time_started, pi.cycle_time)),
    avg_resolution_time_hours: formatHours(pi.avg_resolution_time),
    avg_touch_time_hours: formatHours(pi.avg_touch_time),
    avg_wait_time_hours: formatHours(pi.avg_wait_time),
    avg_admin_time_hours: formatHours(pi.avg_admin_time),
    avg_queue_time_hours: formatHours(pi.avg_queue_time),
    flow_efficiency_percent:
      pi.avg_flow_efficiency !== null && pi.avg_flow_efficiency !== undefined && Number.isFinite(Number(pi.avg_flow_efficiency))
        ? Number((Number(pi.avg_flow_efficiency) * 100).toFixed(2))
        : null,
    validity: {
      avg_cycle_time_hours: validity.avg_cycle_time_started ?? validity.cycle_time ?? (pi.avg_cycle_time_started != null || pi.cycle_time != null),
      avg_resolution_time_hours: validity.avg_resolution_time ?? (pi.avg_resolution_time != null),
      avg_touch_time_hours: validity.avg_touch_time ?? (pi.avg_touch_time != null),
      avg_wait_time_hours: validity.avg_wait_time ?? (pi.avg_wait_time != null),
      avg_admin_time_hours: validity.avg_admin_time ?? (pi.avg_admin_time != null),
      avg_queue_time_hours: validity.avg_queue_time ?? (pi.avg_queue_time != null),
      flow_efficiency_percent: validity.avg_flow_efficiency ?? (pi.avg_flow_efficiency != null),
    },
  };

  const effectiveTimeIntelligence = contextualizeTimeIntelligence(timeIntelligence, normalized.activityContext);

  const sortByDesc = (rows, getter) => [...(Array.isArray(rows) ? rows : [])].sort((a, b) => (getter(b) ?? -Infinity) - (getter(a) ?? -Infinity));
  const volumeOfTransition = (r) =>
    toNumber(r['Work items']) ?? toNumber(r['Work Items']) ?? toNumber(r.Tasks) ?? toNumber(r.Count) ?? 0;
  const delayOfTransition = (r) =>
    toNumber(r['Avg deviation (hrs)']) ?? toNumber(r['Avg Duration (hrs)']) ?? toNumber(r.avg_duration_hrs) ?? 0;
  const pctOfTransition = (r) =>
    toNumber(r.pct_total_tasks) ?? toNumber(r['% total Tasks']) ?? toNumber(r['% total work items']) ?? null;

  const allTransitions = Array.isArray(normalized.transitionsJSON) ? normalized.transitionsJSON : [];

  const topTransitions = sortByDesc(allTransitions, volumeOfTransition).slice(0, 10).map((r) => ({
    transition: r.Transition || r.transition || null,
    from: r['From Activity'] || r.from_activity || null,
    to: r['To Activity'] || r.to_activity || null,
    work_items: volumeOfTransition(r),
    pct_total_items: pctOfTransition(r),
    avg_duration_hours: delayOfTransition(r),
    std_deviation_hours: toNumber(r['Std deviation (hrs)']) ?? toNumber(r['Std Deviation (hrs)']) ?? toNumber(r.std_deviation_hrs),
    is_rework: r['Is rework?'] === true || r['Is Rework'] === true || r.loop === true,
    transition_type: r.transition_type || null,
    from_time_bucket: r.from_time_bucket || null,
    to_time_bucket: r.to_time_bucket || null,
  }));

  const topDelayTransitions = sortByDesc(allTransitions, delayOfTransition).slice(0, 10).map((r) => ({
    transition: r.Transition || r.transition || null,
    from: r['From Activity'] || r.from_activity || null,
    to: r['To Activity'] || r.to_activity || null,
    work_items: volumeOfTransition(r),
    pct_total_items: pctOfTransition(r),
    avg_duration_hours: delayOfTransition(r),
    is_rework: r['Is rework?'] === true || r['Is Rework'] === true || r.loop === true,
    transition_type: r.transition_type || null,
    from_time_bucket: r.from_time_bucket || null,
    to_time_bucket: r.to_time_bucket || null,
  }));

  const reworkTransitions = allTransitions.filter((r) => r['Is rework?'] === true || r['Is Rework'] === true || r.loop === true);
  const topReworkTransitions = sortByDesc(reworkTransitions, volumeOfTransition).slice(0, 10).map((r) => ({
    transition: r.Transition || r.transition || null,
    from: r['From Activity'] || r.from_activity || null,
    to: r['To Activity'] || r.to_activity || null,
    work_items: volumeOfTransition(r),
    pct_total_items: pctOfTransition(r),
    avg_duration_hours: delayOfTransition(r),
    transition_type: r.transition_type || null,
  }));

  const allVariants = Array.isArray(normalized.variantsJSON?.rows) ? normalized.variantsJSON.rows : [];
  const pctOfVariant = (r) => toNumber(r['% Total Tasks']) ?? toNumber(r['% total Tasks']) ?? toNumber(r.pct_total_tasks) ?? 0;
  const loopsOfVariant = (r) => toNumber(r.Loops) ?? 0;
  const durOfVariant = (r) => toNumber(r['Avg Duration (hrs)']) ?? toNumber(r.avg_duration_hrs) ?? 0;
  const stdOfVariant = (r) => toNumber(r['Std Deviation (hrs)']) ?? toNumber(r.std_deviation_hrs) ?? 0;

  const topVariants = sortByDesc(allVariants, pctOfVariant).slice(0, 8).map((r) => ({
    path_name: r.Variant || r.variant || null,
    variant_id: r.ID || r.variant_id || null,
    work_items: toNumber(r.Tasks) ?? toNumber(r['Tasks']) ?? toNumber(r['Work Items']) ?? null,
    pct_total_items: pctOfVariant(r),
    avg_duration_hours: durOfVariant(r),
    std_deviation_hours: stdOfVariant(r),
    loops: loopsOfVariant(r),
    is_rework: r['Is Rework'] === true || r.loop === true || loopsOfVariant(r) > 0,
  }));

  const reworkVariants = topVariants.filter((r) => r.is_rework).slice(0, 6);

  // For automation: expose richer work-item and field signals for trigger/condition design
  const fieldRows = Array.isArray(normalized.fieldUsage?.fields) ? normalized.fieldUsage.fields : [];
  const sampleRows = Array.isArray(normalized.sampleItems?.rows) ? normalized.sampleItems.rows : [];
  const workItemSignals = buildWorkItemSignals({
    sampleItems: sampleRows,
    fieldUsage: fieldRows,
    totalTasks: processSummary.total_items ?? normalized.dataCoverage?.total_tasks ?? 0,
  });

  const automationFieldSignals = {
    field_usage_highlights: (workItemSignals.field_usage_summary.field_usage_highlights || []).slice(0, 30),
    sparse_fields: workItemSignals.field_usage_summary.sparse_fields || [],
    highly_used_fields: workItemSignals.field_usage_summary.highly_used_fields || [],
    classification_fields: workItemSignals.field_usage_summary.classification_fields || [],
    category_breakdowns: workItemSignals.category_breakdowns,
    sample_item_observations: workItemSignals.sample_item_observations,
    item_attribute_summary: workItemSignals.sample_item_summary,
  };

  const slaSignals = buildSlaSignals({
    sampleItems: sampleRows,
    fieldUsage: fieldRows,
    totalTasks: processSummary.total_items ?? normalized.dataCoverage?.total_tasks ?? 0,
  });

  const { activityModel, statusModel, isLifecycle } = buildActivityAndStatusModels(normalized, normalized.activityContext);

  // Waiting/queue statuses are prime automation trigger candidates
  const waitingStatuses = (activityModel.activities || []).filter((s) => {
    const bucket = (s.time_bucket || s.timeBucket || s.operational_type || '').toLowerCase();
    return bucket.includes('wait') || bucket.includes('queue') || bucket.includes('pending');
  });

  const dataCoverage = {
    process_metrics: !!(processSummary.avg_duration_hours != null || processSummary.rework_rate_percent != null || processSummary.variants_total != null),
    transitions: topTransitions.length > 0,
    variants: topVariants.length > 0,
    field_usage: fieldRows.length > 0 || sampleRows.length > 0,
    activity_model: (activityModel.activities || []).length > 0,
    status_types: isLifecycle && (statusModel?.statuses || []).length > 0,
    time_intelligence: Object.values(effectiveTimeIntelligence.validity || {}).some(Boolean),
    sla_metrics: slaSignals.available === true,
    evidence_base_label: [
      processSummary.avg_duration_hours != null || processSummary.rework_rate_percent != null ? 'metrics' : null,
      topTransitions.length ? 'transitions' : null,
      topVariants.length ? 'variants' : null,
      fieldRows.length ? 'field usage' : null,
      sampleRows.length ? 'sample items' : null,
      (activityModel.activities || []).length ? 'activity model' : null,
      Object.values(effectiveTimeIntelligence.validity || {}).some(Boolean) ? 'time intelligence' : null,
      slaSignals.available ? 'task_sla metrics' : null,
    ].filter(Boolean).join(', ') || 'limited evidence',
    notes: [
      sampleRows.length > 0 ? `Sample item evidence available for ${sampleRows.length} items` : null,
    ].filter(Boolean),
  };

  return {
    context: {
      workflow_name: issueTypeStr || '',
      entity_name: 'work items',
      total_items: processSummary.total_items ?? normalized.dataCoverage?.total_tasks ?? null,
    },
    process_summary: processSummary,
    time_intelligence: effectiveTimeIntelligence,
    activity_context: normalized.activityContext,
    activity_model: activityModel,
    status_model: statusModel,
    waiting_statuses: waitingStatuses,
    transition_signals: {
      top_transitions: topTransitions,
      top_delay_transitions: topDelayTransitions,
      top_rework_transitions: topReworkTransitions,
    },
    variant_signals: {
      top_variants: topVariants,
      rework_variants: reworkVariants,
      long_tail_summary: {
        variants_total: normalized.variantsJSON?.variants_total ?? null,
        variants_shown: normalized.variantsJSON?.variants_shown ?? null,
        variants_shown_pct_of_total: normalized.dataCoverage?.variants_shown_pct_of_total ?? null,
        shown_variant_task_coverage_percent: normalized.dataCoverage?.shown_variant_task_coverage_percent ?? null,
      },
    },
    automation_field_signals: automationFieldSignals,
    sla_signals: slaSignals,
    data_coverage: dataCoverage,
  };
};

export const buildImplementationRoadmapInputEvidence = (args = {}) =>
  buildWorkflowRedesignInputEvidence(args);


export const buildConsultingReportInputEvidence = (args = {}) =>
  buildWorkflowRedesignInputEvidence(args);


export const buildAIOpportunitiesInputEvidence = (args = {}) => buildImprovementOpportunitiesInputEvidence(args);
